<template>
  <section class="commercial-form">
    <former-alert
      v-if="removeFailed"
      class="commercial-form-remove-alert"
      type="error"
    >
      Não foi possível remover a regra. Tente novamente.

      <span
        v-if="removeDetails"
        class="commercial-form-remove-details"
      >
        {{ removeDetails }}
      </span>
    </former-alert>

    <former-form
      :sections="sections"
      :loading="loading"
      :button="button"

      @error="setFieldErrors"
      @change="setFieldValue"
      @edit="setField"
      @submit="submit"
      @search="search"
    >
      <template v-slot:footer>
        <former-button
          v-if="removable"
          class="commercial-form-remove"
          color="red"
          icon="trash"
          :disabled="removing || loading"
          padded
          @click="openRemove"
        >
          Remover
        </former-button>
      </template>
    </former-form>

    <popup-confirm
      v-if="removeOpen"
      color="red"
      title="Remover regra"
      :loading="removing"
      @close="closeRemove"
      @confirm="remove"
    >
      Você tem certeza?
    </popup-confirm>
  </section>
</template>

<script>
  import { mapState } from 'vuex';

  import { INITIAL_STORES_FILTERS } from '@/store/modules/stores';
  import { vehicleSearchFilters } from '@/store/modules/vehicle-models';

  import former from '@/helpers/mixins/former';
  import preload from '@/helpers/mixins/preload';
  import debounce from '@/helpers/misc/debounce';
  import scrollHelper from '@/helpers/views/scroll';
  import dataHelper from '@/_CRUDL/helpers/data/items';

  import commercialMap, { storesMap } from '@/views/Settings/Commercial/Form/maps';
  import { defaultSectionIDs } from '@/views/Settings/Commercial/Form/sections';

  import FormerAlert from '@/components/Former/Alert.vue';
  import FormerButton from '@/components/Former/Button.vue';
  import FormerForm from '@/components/Former/Form.vue';
  import PopupConfirm from '@/components/Popup/Confirm.vue';

  export default {
    name: 'CommercialForm',
    components: {
      FormerAlert,
      FormerButton,
      FormerForm,
      PopupConfirm,
    },
    mixins: [
      former,
      preload,
    ],
    props: {
      loading: {
        type: Boolean,
        default() {
          return false;
        },
      },
      sections: {
        type: Array,
        default() {
          return [];
        },
      },
      /*
       * A LOJA DA REGRA QUE A TELA ABRIU, quando há regra: as três coleções serializam a
       * loja junto (`has_one :store`), então editando o nome já veio e não custa request.
       * Criando ela é nula - só existe o id no caminho.
       *
       * Chega como prop e não por `$refs` porque a ordem entre as duas respostas não é
       * garantida: a lista de lojas pode assentar antes ou depois da regra, e o `watch`
       * reaplica as opções em qualquer das duas ordens.
       */
      selectedStore: {
        type: Object,
        default: null,
      },
      /*
       * A REGRA QUE A TELA ABRIU, quando há regra: é dela que sai o id a remover, a loja
       * para onde voltar depois e o `main`. Criando, e enquanto o `read` não assenta, ela
       * é vazia - e é isso que mantém a remoção fora da tela nos dois casos, sem consultar
       * a rota.
       */
      rule: {
        type: Object,
        default() {
          return {};
        },
      },
      // A action Vuex que desativa a regra desta coleção (`<módulo>/delete`). Vazia, a
      // tela não oferece remoção.
      removeAction: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        removeOpen: false,
        removing: false,
        removeFailed: false,
        removeDetails: '',
      };
    },
    computed: {
      ...mapState([
        'stores',
        'vehicleModels',
        'banksIntegrated',
      ]),

      button() {
        return {
          icon: 'check',
          label: 'Salvar regra',
        };
      },

      /*
       * A REGRA PRINCIPAL NÃO SE REMOVE, e é a mesma condição que a listagem tinha antes de
       * migrar para o `embed`: é nela que o `BestFinancingRuleFinder` cai quando não acha
       * uma mais específica. Tabela de preço não tem a coluna - lá o campo não vem, e a
       * remoção segue oferecida.
       */
      removable() {
        return !!this.removeAction && !!this.rule.id && !this.rule.main;
      },
    },
    watch: {
      selectedStore() {
        this.setStoreOptions(this.stores.list.items);
      },
    },
    created() {
      this.searchStoresDebounced = debounce(
        this.searchStores,
        (value, paginate) => (paginate ? 1000 : 300),
      );

      this.searchVehiclesDebounced = debounce(this.getVehicles, 300);
    },
    mounted() {
      this.searchStores('', false);

      this.setFieldCallback(storesMap.store_id, this.changedStore);

      if (this.getField(commercialMap.has_bank)) {
        this.setFieldCallback(commercialMap.has_bank, this.changedIncludeBank);
      }

      if (this.getField(commercialMap.has_vehicle)) {
        this.setFieldCallback(commercialMap.has_vehicle, this.changedIncludeVehicle);
      }

      this.changedStore();
    },
    beforeDestroy() {
      this.searchStoresDebounced.cancel();
      this.searchVehiclesDebounced.cancel();
    },
    methods: {
      search(id, value, paginate) {
        switch (id) {
          case storesMap.store_id:
            this.searchStoresDebounced(value, paginate);
            break;
          case commercialMap.vehicle_model_id:
            if (!paginate) {
              this.searchVehiclesDebounced(value);
            }
            break;
          default:
            break;
        }
      },

      searchStores(value, paginate) {
        const { dispatch } = this.$store;
        const { filters } = this.stores.list;

        dispatch('stores/filter', {
          ...filters,

          q: value,
          page: paginate ? (filters.page || 1) + 1 : 1,
          CRUDL_append: paginate,
        }).then(() => this.reloadStores(this.stores.list.filters));
      },

      clearOptions() {
        this.$store.dispatch('vehicleModels/clear', { action: 'list' });
        this.$store.dispatch('banksIntegrated/clear', { action: 'list' });
      },
      changedStore() {
        this.clearOptions();

        const store = this.getFieldValue(storesMap.store_id);

        this.toggleSection(defaultSectionIDs.commercial_association, !store);
        this.toggleSection(defaultSectionIDs.commercial_settings, !store);
        this.toggleSection(defaultSectionIDs.commercial_products, !store);
        this.toggleSection(defaultSectionIDs.commercial_priorities, !store);

        if (store) {
          if (!this.getField(commercialMap.has_bank)
            && this.getField(commercialMap.bank_id)) {
            this.getBanks(store);
          } else if (this.getField(commercialMap.has_bank)) {
            this.changedIncludeBank();
          }

          if (!this.getField(commercialMap.has_vehicle)
            && this.getField(commercialMap.vehicle_model_id)) {
            this.getVehicles();
          } else if (this.getField(commercialMap.has_vehicle)) {
            this.changedIncludeVehicle();
          }
        }
      },
      changedIncludeBank() {
        const includeBank = this.getFieldValue(commercialMap.has_bank);
        this.setFieldSkip(commercialMap.bank_id, !includeBank);

        if (includeBank && !Object.keys(this.banksIntegrated.list.items).length) {
          this.getBanks(this.getFieldValue(storesMap.store_id));
        }
      },
      changedIncludeVehicle() {
        const includeVehicle = this.getFieldValue(commercialMap.has_vehicle);
        this.setFieldSkip(commercialMap.vehicle_model_id, !includeVehicle);

        if (includeVehicle && !Object.keys(this.vehicleModels.list.items).length) {
          this.getVehicles();
        }
      },

      reloadStores(filters) {
        this.getStores(false, filters);
      },
      getStores(cached, payload) {
        this.setFieldProcessing(storesMap.store_id, true);

        this.preloadStores(cached, {
          ...payload,
          per_page: INITIAL_STORES_FILTERS.per_page,
        }).then(this.gotStores);
      },
      gotStores() {
        const {
          items,
        } = this.stores.list;

        this.setStoreOptions(items);
        this.setFieldProcessing(storesMap.store_id, false);

        const ids = Object.keys(items);

        if (ids.length === 1) {
          this.setFieldValue(storesMap.store_id, items[ids[0]].id);
        }

        this.reachSelectedStore(items);
      },

      /*
       * A LOJA ESCOLHIDA ENTRA NAS OPÇÕES MESMO FORA DA PÁGINA, e é o que faz o seletor
       * conseguir mostrá-la.
       *
       * O rótulo sai de procurar o valor em `field.options` (`Former/Field/Select.vue`), e
       * as opções são UMA PÁGINA de lojas - as vinte primeiras em ordem alfabética. Numa
       * conta grande a loja que veio do caminho costuma estar fora dela: o valor está certo
       * e o envio vai para a loja certa, mas o campo mostra "Selecione uma loja". Quem
       * tentar "consertar" escolhendo uma loja visível salva a regra na LOJA ERRADA, e o
       * redirect de depois confirma o engano levando para a listagem dela.
       *
       * Ela é acrescentada e não fundida com o que já estava: o seletor busca, e as opções
       * são substituídas a cada busca. Guardar as anteriores deixaria resultado velho na
       * lista.
       */
      setStoreOptions(items, reached = this.selectedStore) {
        const options = reached && reached.id
          ? { ...items, [reached.id]: reached }
          : items;

        this.setFieldOptions(storesMap.store_id, options);
      },

      /*
       * E QUANDO NÃO HÁ ENTIDADE PARA DIZER O NOME DA LOJA, ela é buscada - uma chamada, e
       * só nesse caso.
       *
       * Editando, o nome vem de graça: as três coleções serializam a loja junto da regra
       * (`has_one :store`), e a tela passa esse objeto no `selectedStore`. Criando não há
       * regra nenhuma, o caminho traz só o id, e sem o nome não há o que mostrar.
       *
       * O `:rule_id` DO CAMINHO É O QUE DIZ QUE HÁ REGRA A CAMINHO, e não o `selectedStore`
       * estar preenchido: as duas respostas são independentes, e quando a lista de lojas
       * assenta primeiro ele ainda é nulo - editando, isso disparava uma busca por uma loja
       * que ia chegar de graça um instante depois. Quem preenche as opções nessa ordem é o
       * `watch`.
       */
      reachSelectedStore(items) {
        const storeID = this.getFieldValue(storesMap.store_id);

        if (false
          || !storeID
          || this.selectedStore
          || this.$route.params.rule_id
          || Object.values(items).some(store => `${store.id}` === `${storeID}`)
        ) {
          return;
        }

        this.$store
          .dispatch('stores/read', { id: storeID })
          .then(() => this.setStoreOptions(items, this.stores.read.item))
          .catch(() => {});
      },

      getBanks(storeId) {
        this.setFieldProcessing(commercialMap.bank_id, true);

        this.$store
          .dispatch('banksIntegrated/list', {
            store_id: storeId,
          })
          .then(this.gotBanks);
      },
      gotBanks() {
        const id = commercialMap.bank_id;
        const items = dataHelper(this.banksIntegrated.list.items);

        this.setFieldOptions(id, items, 'name', 'id');
        this.setFieldProcessing(id, false);
      },
      getVehicles(query) {
        const id = commercialMap.vehicle_model_id;
        const searching = typeof query === 'string';

        this.setFieldProcessing(id, true);

        this.preloadVehicleModels(!searching, searching ? vehicleSearchFilters(query) : {})
          .then(this.gotVehicles)
          .catch(() => this.setFieldProcessing(id, false));
      },
      gotVehicles() {
        const key = commercialMap.vehicle_model_id;

        const {
          items,
        } = this.vehicleModels.list;

        this.setFieldOptions(key, Object.keys(items).reduce((state, id) => {
          const item = items[id];

          if (item.name && item.year_start && item.year_end) {
            state[id] = {
              ...item,
              name: item.version ? `${item.name} - ${item.version}` : item.name,
            };
          }

          return state;
        }, {}));

        this.setFieldProcessing(key, false);
      },
      submit() {
        // A recusa de uma remoção anterior não tem mais o que dizer: a tela está fazendo
        // outra coisa, e a faixa apareceria empilhada com os erros do salvamento.
        this.removeFailed = false;
        this.removeDetails = '';

        this.$emit('submit');
      },

      /*
       * UM FLUXO DE CADA VEZ. Salvar não bloqueia a tela, e o `Popup` é servido por um
       * `portal-target` sem `multiple` (`App.vue`) - só o último registrado aparece. Sem
       * esta trava, abrir a remoção durante um salvamento com confirmação sumiria com a
       * caixa da remoção debaixo do cursor, e confirmar as duas mandaria dois pedidos e
       * duas navegações para a mesma regra.
       */
      openRemove() {
        if (this.loading) {
          return;
        }

        this.removeOpen = true;
      },
      closeRemove() {
        this.removeOpen = false;
      },

      /*
       * "REMOVER" NA TELA É DESATIVAR NO SERVIDOR: as três coleções expõem
       * `GET /<coleção>/:id/deactivate`, e é o que a action do `_CRUDL` chama.
       */
      remove() {
        this.removing = true;
        this.removeFailed = false;
        this.removeDetails = '';

        this.$store
          .dispatch(this.removeAction, { id: this.rule.id })
          .then(this.removeSuccess)
          .catch(this.removeFailure);
      },

      /*
       * DE VOLTA À LISTAGEM DA LOJA DA REGRA - e aqui, ao contrário do salvar, não é a loja
       * do campo: o seletor pode ter sido mexido sem salvar, e a regra removida é da loja
       * em que ela estava. A loja do caminho cobre o caso de ela não vir serializada.
       */
      removeSuccess() {
        this.removing = false;
        this.removeOpen = false;

        this.$emit('removed', this.rule.store_id || this.$route.params.id);
      },

      // O erro da remoção não é de campo, então não passa pelo `setErrors`: ele vira a
      // faixa no topo, e por isso a tela sobe.
      removeFailure() {
        const [namespace] = this.removeAction.split('/');
        const { error } = this.$store.state[namespace].delete;

        this.removing = false;
        this.removeOpen = false;
        this.removeFailed = true;
        this.removeDetails = typeof error === 'string' ? error : '';

        scrollHelper();
      },
    },
  };
</script>

<style lang="scss" scoped>
  .commercial-form-remove-details {
    display: block;
  }
</style>
